import { ContactLookupSettings } from "@signalist/shared-types";

import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
	useStartPreviewMutation,
	useGetPreviewStatusQuery,
	useGetPreviewResultsQuery,
	useStopPreviewMutation,
	useRerunPreviewMutation,
	useExtendPreviewMutation,
} from "@/pages/Private/redux/listPreview/listPreview.api";
import {
	clearPreview,
	listPreviewSelector,
} from "@/pages/Private/redux/listPreview/listPreview.slice";

import { ListPreviewStatus, PreviewRerunMode } from "../schema/listPreview";
import { PreviewActivate, PreviewCompanyCount } from "./PreviewActivate";
import { PreviewRunning } from "./PreviewRunning";
import { PreviewResultsTable } from "./PreviewResultsTable";
import { PreviewOutcomeFooter } from "./PreviewOutcomeFooter";
import { RerunPreviewModal } from "./RerunPreviewModal";

const POLLING_INTERVAL_MS = 3000;

interface PreviewSectionProps {
	listId: number;
	/** The list's contact lookup settings, which the preview runs and its confirm quotes. */
	contactLookups: ContactLookupSettings;
	onBeforeAction?: () => Promise<void>;
}

export const PreviewSection: FunctionComponent<PreviewSectionProps> = ({
	listId,
	contactLookups,
	onBeforeAction,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const [showRerunModal, setShowRerunModal] = useState(false);

	const { activePreview, results, isPolling } = useAppSelector(listPreviewSelector);

	// Clear stale preview data when switching between lists
	useEffect(() => {
		dispatch(clearPreview());
	}, [listId, dispatch]);

	const [startPreview, { isLoading: isStartLoading }] = useStartPreviewMutation();
	const [stopPreview, { isLoading: isStopLoading }] = useStopPreviewMutation();
	const [rerunPreview, { isLoading: isRerunLoading }] = useRerunPreviewMutation();
	const [extendPreview, { isLoading: isExtendLoading }] = useExtendPreviewMutation();

	// Initial fetch — no built-in polling; we poll manually below
	const { refetch: refetchStatus } = useGetPreviewStatusQuery(listId);

	const { refetch: refetchResults } = useGetPreviewResultsQuery(
		{ listId, page: 1, limit: 50 },
		{ skip: !activePreview }
	);

	// Manual polling via setInterval for reliable status + results refresh
	const refetchStatusRef = useRef(refetchStatus);
	const refetchResultsRef = useRef(refetchResults);

	useEffect(() => {
		refetchStatusRef.current = refetchStatus;
	}, [refetchStatus]);

	useEffect(() => {
		refetchResultsRef.current = refetchResults;
	}, [refetchResults]);

	useEffect(() => {
		if (!isPolling) {
			return;
		}

		const id = setInterval(() => {
			refetchStatusRef.current();
			refetchResultsRef.current();
		}, POLLING_INTERVAL_MS);

		return () => clearInterval(id);
	}, [isPolling]);

	// A refusal rejects into the credit-confirm Start Preview opens, which shows it (PREC-4263).
	const handleStart = useCallback(
		async (companyCount: PreviewCompanyCount) => {
			if (onBeforeAction) {
				await onBeforeAction();
			}

			// arch-allow unwrap-caught: PreviewCreditConfirm's CreditConfirmModal catches and shows it
			await startPreview({ listId, body: { companyCount } }).unwrap();
		},
		[listId, startPreview, onBeforeAction]
	);

	const handleStop = useCallback(async () => {
		try {
			await stopPreview(listId).unwrap();
		} catch (error) {
			console.error("Failed to stop preview:", error);
		}
	}, [listId, stopPreview]);

	const handleRerun = useCallback(
		async (mode: PreviewRerunMode) => {
			try {
				if (onBeforeAction) {
					await onBeforeAction();
				}

				await rerunPreview({ listId, body: { mode } }).unwrap();
				setShowRerunModal(false);
			} catch (error) {
				console.error("Failed to rerun preview:", error);
			}
		},
		[listId, rerunPreview, onBeforeAction]
	);

	// As Start's, a refusal rejects into the credit-confirm Extend opens, which shows it (PREC-5028).
	const handleExtend = useCallback(
		async (additionalCount: 10 | 20 | 50) => {
			if (onBeforeAction) {
				await onBeforeAction();
			}

			// arch-allow unwrap-caught: PreviewExtendCreditConfirm's CreditConfirmModal catches and shows it
			await extendPreview({ listId, body: { additionalCount } }).unwrap();
		},
		[listId, extendPreview, onBeforeAction]
	);

	const isActive =
		activePreview?.status === ListPreviewStatus.PENDING ||
		activePreview?.status === ListPreviewStatus.RUNNING;

	const isDone =
		activePreview?.status === ListPreviewStatus.COMPLETED ||
		activePreview?.status === ListPreviewStatus.STOPPED ||
		activePreview?.status === ListPreviewStatus.FAILED;

	return (
		<div className="mt-6">
			{/* Show activate card when no preview or preview is done */}
			{(!activePreview || isDone) && (
				<PreviewActivate
					contactLookups={contactLookups}
					disabled={isDone}
					isLoading={isStartLoading}
					onStart={handleStart}
				/>
			)}

			{/* Show running state */}
			{activePreview && isActive && (
				<PreviewRunning isStopLoading={isStopLoading} preview={activePreview} onStop={handleStop} />
			)}

			{/* Show results table when we have results */}
			{results.length > 0 && <PreviewResultsTable results={results} />}

			{/* Show outcome footer when done */}
			{activePreview && isDone && (
				<PreviewOutcomeFooter
					contactLookups={contactLookups}
					isExtendLoading={isExtendLoading}
					preview={activePreview}
					onExtend={handleExtend}
					onRerun={() => setShowRerunModal(true)}
				/>
			)}

			{/* Rerun modal */}
			<RerunPreviewModal
				isLoading={isRerunLoading}
				isOpen={showRerunModal}
				onClose={() => setShowRerunModal(false)}
				onConfirm={handleRerun}
			/>
		</div>
	);
};
